/**
 * Mock mode for Microsoft AppSource certification testing.
 *
 * When the user enters MOCK_BASE_URL as their "Odoo URL", all API calls are
 * intercepted here and return realistic fake data instead of hitting a real
 * server. Office.js still runs normally inside real Outlook, so the email
 * context (sender, recipients) comes from the actual open email.
 *
 * To activate: enter  https://demo.2bitcloud.ch  in the "Database URL" field.
 */

export const MOCK_BASE_URL = "https://demo.2bitcloud.ch";
declare const __DEV__: boolean;

export function isMockMode(): boolean {
  if (__DEV__ && typeof window !== "undefined" && window.location.pathname.endsWith("/standalone.html")) {
    return true;
  }
  try {
    return localStorage.getItem("baseURL") === MOCK_BASE_URL;
  } catch {
    return false;
  }
}

/**
 * Opens an Odoo record URL in a new tab.
 * In mock/demo mode this is a no-op — the URL points to a non-existent server.
 *
 * Pass `existingWindow` when the caller already opened a blank tab (popup-
 * blocker workaround used in Section.tsx) so it gets closed cleanly instead
 * of left dangling.
 */
export function openOdooUrl(url: string, existingWindow?: Window | null): void {
  if (isMockMode()) {
    if (existingWindow && !existingWindow.closed) existingWindow.close();
    return;
  }
  if (existingWindow && !existingWindow.closed) {
    existingWindow.location.href = url;
  } else {
    window.open(url, "_blank");
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ok(payload: unknown): string {
  return JSON.stringify({ success: true, data: payload });
}

/** Derive a display name from an email address. */
function nameFromEmail(email: string): string {
  if (!email) return "Demo Contact";
  const local = email.split("@")[0];
  return local
    .replace(/[._+-]+/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Try to pull the email value out of an Odoo-style search domain that looks
 * like:  { args: [[["email", "=", "alice@example.com"]]] }
 */
function extractEmailFromArgs(data?: object): string {
  try {
    const args = (data as { args?: unknown[] })?.args;
    if (!Array.isArray(args) || !Array.isArray(args[0])) return "";
    const domain: unknown[] = args[0];
    for (const clause of domain) {
      if (
        Array.isArray(clause) &&
        typeof clause[0] === "string" &&
        clause[0].includes("email") &&
        typeof clause[2] === "string" &&
        clause[2]
      ) {
        return clause[2];
      }
    }
  } catch {
    // ignore
  }
  return "";
}

// Track the last looked-up email so that subsequent getPartner/{id} calls
// can return a consistent partner with the correct email address.
let _lastEmail = "alice.johnson@acme-corp.com";
const _partnersById: Record<number, any> = {};

function stableId(value: string, offset: number): number {
  let hash = 0;
  for (let index = 0; index < value.length; index++) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return offset + Math.abs(hash % 8000);
}

function buildCompanyForEmail(email: string) {
  const domain = (email.split("@")[1] || "example.com").toLowerCase();
  const companyName = domain
    .split(".")[0]
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, character => character.toUpperCase());
  const company = {
    id: stableId(domain, 10000),
    name: `${companyName} Corporation`,
    display_name: `${companyName} Corporation`,
    email: `info@${domain}`,
    phone: "+1 (415) 555-0100",
    website: `https://${domain}`,
    street: "742 Evergreen Terrace",
    city: "San Francisco",
    zip: "94102",
    country_id: [233, "United States"],
    image_1920: false,
    is_company: true,
    can_write_on_partner: true,
  };
  _partnersById[company.id] = company;
  return company;
}

function buildCompanyPartner(company: any, matchingEmail?: string) {
  const persistedCompanyPartner = {
    ...company,
    company: company,
    parent_id: false,
    commercial_partner_id: company.id,
    company_id: [1, "Demo Odoo Company"],
    can_create_partner: true,
    can_create_project: true,
    user_companies: [1],
  };
  _partnersById[company.id] = persistedCompanyPartner;
  return matchingEmail
    ? { ...persistedCompanyPartner, email: matchingEmail }
    : persistedCompanyPartner;
}

function buildPartner(email: string, id = stableId(email, 1000)) {
  const name = nameFromEmail(email) || "Demo Contact";
  const company = buildCompanyForEmail(email);
  const partner = {
    id,
    name,
    email,
    phone: "+1 (415) 555-0192",
    mobile: "+1 (415) 555-0847",
    title: "Head of Operations",
    is_company: false,
    company_id: [1, "Demo Odoo Company"],
    parent_id: [company.id, company.name],
    commercial_partner_id: company.id,
    company,
    image_1920: false,
    can_write_on_partner: true,
    can_create_partner: true,
    can_create_project: true,
    user_companies: [1],
    street: "742 Evergreen Terrace",
    city: "San Francisco",
    zip: "94102",
    country_id: [233, "United States"],
  };
  _partnersById[id] = partner;
  return partner;
}

// ── Post-create record builder ────────────────────────────────────────────────

/**
 * Builds a minimal record object (using mapped class-property names, not raw
 * Odoo JSON keys) to append to a Section's visible list after a mock create.
 *
 * @param model  Odoo model name, e.g. "crm.lead"
 * @param name   Display name — callers pass the real email subject
 * @param extra  Any additionalValues forwarded from the Section's onClickCreate
 */
export function buildMockSectionRecord(
  model: string,
  name: string,
  extra?: Record<string, unknown>
): Record<string, unknown> | null {
  const id = Date.now();

  switch (model) {
    case "crm.lead":
      return {
        id,
        name,
        type: (extra?.type as string) ?? "lead",
        emailFrom: "",
        partnerId: undefined,
        probability: 0,
        expectedRevenue: "0",
      };
    case "helpdesk.ticket":
      return { id, name, isClosed: false };
    case "project.task": {
      const projectId = (extra?.project_id as number) ?? 11;
      return { id, name, projectId, projectName: "Enterprise Accounts", companyId: 1 };
    }
    case "account.move":
      return { id, name: "INV/2026/NEW", amount_total: 0, currency_id: [1, "USD"], companyId: 1 };
    case "sale.order":
      return { id, name: "S00/NEW", amount_total: 0, currency_id: [1, "USD"], companyId: 1 };
    case "purchase.order":
      return { id, name: "P00/NEW", amount_total: 0, currency_id: [1, "USD"], companyId: 1 };
    default:
      return null;
  }
}

// ── Main dispatcher ───────────────────────────────────────────────────────────

/**
 * Returns a JSON string response for the given URL + request body, or null if
 * the URL is not matched (caller should fall through to a real request).
 */
export function mockRequest(url: string, data?: object): string | null {
  const path = url.startsWith(MOCK_BASE_URL) ? url.slice(MOCK_BASE_URL.length) : url;

  // ── Partners ──────────────────────────────────────────────────────────────

  if (path === "/api/v1/outlook/res.partner/call/search_read") {
    const email = extractEmailFromArgs(data) || _lastEmail;
    if (email) _lastEmail = email;
    const contact = buildPartner(email);
    // Deliberately return the matching company first. Partner.sortBestMatches
    // must still prioritize the person, mirroring regression #6040.
    return ok([buildCompanyPartner(contact.company, email), contact]);
  }

  if (/^\/api\/v1\/outlook\/res\.partner\/\d+\/call\/read$/.test(path)) {
    const idMatch = path.match(/\/res\.partner\/(\d+)\/call\/read/);
    const id = idMatch ? parseInt(idMatch[1], 10) : stableId(_lastEmail, 1000);
    return ok(_partnersById[id] || buildPartner(_lastEmail, id));
  }

  // POST — create partner
  if (path === "/api/v1/outlook/res.partner") {
    return ok({ id: 999 });
  }

  // ── Companies ─────────────────────────────────────────────────────────────

  if (/^\/api\/v1\/outlook\/res\.company\/\d+\/call\/read$/.test(path)) {
    return ok({
      id: 10,
      name: "Acme Corporation",
      phone: "+1 (415) 555-0100",
      email: "info@acme-corp.com",
      website: "https://www.acme-corp.com",
      street: "742 Evergreen Terrace",
      city: "San Francisco",
      zip: "94102",
      country_id: [233, "United States"],
      image_1920: false,
    });
  }

  // ── CRM Leads / Opportunities ─────────────────────────────────────────────

  if (path === "/api/v1/outlook/crm.lead/call/search_read") {
    const email = extractEmailFromArgs(data) || _lastEmail;
    return ok([
      {
        id: 101,
        name: "Software License Renewal",
        type: "opportunity",
        email_from: email,
        partner_id: [42, nameFromEmail(email)],
        probability: 75,
        expected_revenue: "12500",
      },
      {
        id: 102,
        name: "Enterprise Support Package",
        type: "lead",
        email_from: email,
        partner_id: [42, nameFromEmail(email)],
        probability: 40,
        expected_revenue: "5000",
      },
    ]);
  }

  if (path === "/api/v1/outlook/crm.lead/call/create") {
    return ok({ id: 999 });
  }

  if (path === "/mail_client_extension/lead/create_from_partner") {
    return ok({ id: 999 });
  }

  if (path === "/mail_client_extension/lead/delete") {
    return ok({ id: 999 });
  }

  // ── Invoices ──────────────────────────────────────────────────────────────

  if (path === "/api/v1/outlook/account.move/call/search_read") {
    return ok([
      { id: 201, name: "INV/2026/00042", amount_total: 3750.0, currency_id: [1, "USD"], company_id: 1 },
      { id: 202, name: "INV/2026/00031", amount_total: 1200.0, currency_id: [1, "USD"], company_id: 1 },
    ]);
  }

  if (path === "/api/v1/outlook/account.move/call/create") {
    return ok({ id: 999 });
  }

  // ── Sale Orders ───────────────────────────────────────────────────────────

  if (path === "/api/v1/outlook/sale.order/call/search_read") {
    return ok([
      { id: 301, name: "S00042", amount_total: 8500.0, currency_id: [1, "USD"], company_id: 1 },
      { id: 302, name: "S00038", amount_total: 2300.0, currency_id: [1, "USD"], company_id: 1 },
    ]);
  }

  if (path === "/api/v1/outlook/sale.order/call/create") {
    return ok({ id: 999 });
  }

  // ── Purchase Orders / RFQs ──────────────────────────────────────────────

  if (path === "/api/v1/outlook/purchase.order/call/search_read") {
    return ok([
      { id: 351, name: "P00042", amount_total: 6400.0, currency_id: [1, "USD"], company_id: 1 },
      { id: 352, name: "P00038", amount_total: 1750.0, currency_id: [1, "USD"], company_id: 1 },
    ]);
  }

  if (path === "/api/v1/outlook/purchase.order/call/create") {
    return ok({ id: 999 });
  }

  // ── Tasks ─────────────────────────────────────────────────────────────────

  if (path === "/api/v1/outlook/project.task/call/search_read") {
    return ok([
      { id: 401, name: "Q3 Proposal Follow-up", project_id: [11, "Enterprise Accounts"], company_id: 1 },
      { id: 402, name: "Contract Renewal Discussion", project_id: [11, "Enterprise Accounts"], company_id: 1 },
    ]);
  }

  if (path === "/api/v1/outlook/project.task/call/create") {
    return ok({ id: 999 });
  }

  // ── Projects ──────────────────────────────────────────────────────────────

  if (path === "/api/v1/outlook/project.project/call/search_read") {
    return ok([
      { id: 11, name: "Enterprise Accounts" },
      { id: 12, name: "Customer Success" },
    ]);
  }

  if (path === "/api/v1/outlook/project.project/call/create") {
    return ok({ id: 999 });
  }

  // ── Helpdesk Tickets ──────────────────────────────────────────────────────

  if (path === "/api/v1/outlook/helpdesk.ticket/call/search") {
    return ok([{ id: 501 }, { id: 502 }]);
  }

  if (/^\/api\/v1\/outlook\/helpdesk\.ticket\/\d+\/call\/read$/.test(path)) {
    const idMatch = path.match(/\/helpdesk\.ticket\/(\d+)\/call\/read/);
    const id = idMatch ? parseInt(idMatch[1], 10) : 501;
    const tickets: Record<number, { name: string; close_date: string | false }> = {
      501: { name: "Login issue after password reset", close_date: false },
      502: { name: "PDF export missing company logo", close_date: "2026-04-15 10:30:00" },
    };
    const t = tickets[id] ?? { name: `Support Ticket #${id}`, close_date: false };
    return ok({ id, name: t.name, close_date: t.close_date });
  }

  if (path === "/api/v1/outlook/helpdesk.ticket/call/create") {
    return ok({ id: 999 });
  }

  // ── Mail logging & attachments ────────────────────────────────────────────

  if (path === "/api/v1/outlook/mail.message/call/create") {
    return ok({ id: 999, message_id: 999 });
  }

  if (path === "/api/v1/outlook/ir.attachment/call/create") {
    return ok({ id: 999 });
  }

  // ── Legacy endpoints ──────────────────────────────────────────────────────

  if (path === "/mail_client_extension/modules/get") {
    return JSON.stringify({
      modules: ["crm", "project", "helpdesk", "account", "sale", "purchase", "outlook_purchase"],
    });
  }

  // Catch-all: return an empty success so no real network request is made
  // against the non-existent demo server.
  return ok({});
}
